import React, { useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';

// ── Utility ──────────────────────────────────────────────────────────
function isVisible(field, sectionAnswers) {
  const c = field.conditional;
  if (!c) return true;
  const v = sectionAnswers?.[c.field];
  if ('value' in c) return v === c.value;
  if ('notValue' in c) return v !== c.notValue;
  if ('oneOf' in c) return (c.oneOf || []).includes(v);
  return true;
}

function testId(prefix, section, field) {
  return `${prefix}-${section}-${field}`.replace(/[^a-z0-9-]/gi, '-').toLowerCase();
}

// ── Field renderers ───────────────────────────────────────────────────
function TextField({ field, value, onChange, tid }) {
  return (
    <Input
      type={field.type === 'number' ? 'number' : 'text'}
      value={value ?? ''}
      placeholder={field.placeholder || ''}
      onChange={(e) => {
        const v = field.type === 'number'
          ? (e.target.value === '' ? null : Number(e.target.value))
          : e.target.value;
        onChange(v);
      }}
      data-testid={tid}
      className="bg-white border-[#EBE8E3] text-[#2C2A29]"
    />
  );
}

function TextAreaField({ field, value, onChange, tid }) {
  return (
    <Textarea
      value={value ?? ''}
      placeholder={field.placeholder || ''}
      onChange={(e) => onChange(e.target.value)}
      data-testid={tid}
      className="min-h-[96px] bg-white border-[#EBE8E3] text-[#2C2A29]"
    />
  );
}

function SliderField({ field, value, onChange, tid }) {
  const max = field.max ?? 10;
  const v = typeof value === 'number' ? value : 0;
  const color = v <= (max * 0.3) ? '#4E7A6B' : v <= (max * 0.6) ? '#C8A87B' : '#E07B7B';
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-[#8A8681]">0</span>
        <span className="text-sm font-mono font-semibold" style={{ color }} data-testid={`${tid}-value`}>
          {v} / {max}
        </span>
        <span className="text-xs text-[#8A8681]">{max}</span>
      </div>
      <Slider min={0} max={max} step={1} value={[v]} onValueChange={([nv]) => onChange(nv)}
        data-testid={tid} />
    </div>
  );
}

function SingleField({ field, value, onChange, tid }) {
  return (
    <div className="flex flex-wrap gap-2">
      {(field.options || []).map(opt => {
        const selected = value === opt;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(selected ? null : opt)}
            data-testid={`${tid}-opt-${opt.replace(/\W+/g, '-').toLowerCase()}`}
            className={[
              'px-3 py-1.5 rounded-full text-xs border transition-all',
              selected
                ? 'bg-[#2C2A29] text-white border-[#2C2A29]'
                : 'bg-white text-[#5C5955] border-[#E3DFD7] hover:border-[#C89B7B]',
            ].join(' ')}
          >{opt}</button>
        );
      })}
    </div>
  );
}

function MultiField({ field, value, onChange, tid, cap }) {
  const arr = Array.isArray(value) ? value : [];
  const toggle = (opt) => {
    if (arr.includes(opt)) {
      onChange(arr.filter(x => x !== opt));
    } else {
      if (cap && arr.length >= cap) return;
      onChange([...arr, opt]);
    }
  };
  return (
    <div className="space-y-2">
      {cap ? (
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-[10px]">Select up to {cap}</Badge>
          <span className="text-[11px] text-[#8A8681]">{arr.length}/{cap}</span>
        </div>
      ) : null}
      <div className="flex flex-wrap gap-2">
        {(field.options || []).map(opt => {
          const selected = arr.includes(opt);
          const disabled = cap && !selected && arr.length >= cap;
          return (
            <button
              key={opt}
              type="button"
              onClick={() => toggle(opt)}
              disabled={disabled}
              data-testid={`${tid}-opt-${opt.replace(/\W+/g, '-').toLowerCase()}`}
              className={[
                'px-3 py-1.5 rounded-full text-xs border transition-all',
                selected
                  ? 'bg-[#C89B7B] text-white border-[#C89B7B]'
                  : disabled
                  ? 'bg-[#F5F2EC] text-[#B5B0A8] border-[#EBE8E3] cursor-not-allowed'
                  : 'bg-white text-[#5C5955] border-[#E3DFD7] hover:border-[#C89B7B]',
              ].join(' ')}
            >{opt}</button>
          );
        })}
      </div>
    </div>
  );
}

function CheckboxField({ field, value, onChange, tid }) {
  return (
    <div className="flex items-start gap-2">
      <Checkbox
        checked={!!value}
        onCheckedChange={(c) => onChange(!!c)}
        data-testid={tid}
        className="mt-0.5"
      />
      <span className="text-sm text-[#3B3936] leading-snug">{field.label}</span>
    </div>
  );
}

function FileField({ field, value, onChange, tid }) {
  // Stores filename + base64 for lightweight uploads. Large files should go via
  // a proper object-storage pipeline later. This keeps the intake self-contained.
  const fileName = value?.name || '';
  return (
    <div className="space-y-1.5">
      <input
        type="file"
        accept={field.accept}
        data-testid={tid}
        className="block w-full text-sm text-[#3B3936] file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border file:border-[#E3DFD7] file:bg-white file:text-[#3B3936] hover:file:border-[#C89B7B]"
        onChange={async (e) => {
          const f = e.target.files?.[0];
          if (!f) { onChange(null); return; }
          // Only store metadata client-side to avoid huge base64 blobs in draft.
          onChange({ name: f.name, size: f.size, type: f.type, uploaded_at: new Date().toISOString() });
        }}
      />
      {fileName ? <p className="text-[11px] text-[#4E7A6B]">Attached: {fileName}</p> : null}
    </div>
  );
}

function DateField({ field, value, onChange, tid }) {
  return (
    <Input
      type="date"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      data-testid={tid}
      className="bg-white border-[#EBE8E3] text-[#2C2A29] w-auto"
    />
  );
}

// ── Per-section block ─────────────────────────────────────────────────
function FieldBlock({ field, sectionKey, value, sectionAnswers, onChange, tidPrefix }) {
  if (!isVisible(field, sectionAnswers)) return null;

  const tid = testId(tidPrefix, sectionKey, field.key);
  const type = field.type;

  if (type === 'checkbox') {
    return (
      <div className="rounded-md border border-[#EBE8E3] bg-[#FAF8F4] p-3">
        <CheckboxField field={field} value={value} onChange={onChange} tid={tid} />
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <Label className="text-[#3B3936] text-xs font-medium">
        {field.label}
        {field.required ? <span className="text-[#E07B7B] ml-1">*</span> : null}
      </Label>
      {type === 'text' || type === 'number' ? (
        <TextField field={field} value={value} onChange={onChange} tid={tid} />
      ) : type === 'textarea' ? (
        <TextAreaField field={field} value={value} onChange={onChange} tid={tid} />
      ) : type === 'slider' ? (
        <SliderField field={field} value={value} onChange={onChange} tid={tid} />
      ) : type === 'single' ? (
        <SingleField field={field} value={value} onChange={onChange} tid={tid} />
      ) : type === 'multi' ? (
        <MultiField field={field} value={value} onChange={onChange} tid={tid} />
      ) : type === 'multi3' ? (
        <MultiField field={field} value={value} onChange={onChange} tid={tid} cap={3} />
      ) : type === 'file' ? (
        <FileField field={field} value={value} onChange={onChange} tid={tid} />
      ) : type === 'date' ? (
        <DateField field={field} value={value} onChange={onChange} tid={tid} />
      ) : (
        <p className="text-xs text-[#E07B7B]">Unsupported field type: {type}</p>
      )}
    </div>
  );
}

// ── Section completeness ─────────────────────────────────────────────
export function computeSectionCompleteness(section, answers) {
  const secAns = answers[section.key] || {};
  const visible = (section.fields || []).filter(f => isVisible(f, secAns));
  if (visible.length === 0) return { filled: 0, total: 0, pct: 0 };
  let filled = 0;
  for (const f of visible) {
    const v = secAns[f.key];
    if (f.type === 'checkbox') { if (v) filled++; continue; }
    if (Array.isArray(v)) { if (v.length > 0) filled++; continue; }
    if (typeof v === 'number') { filled++; continue; }
    if (typeof v === 'string' && v.trim()) { filled++; continue; }
    if (v && typeof v === 'object') { filled++; continue; }
  }
  return { filled, total: visible.length, pct: Math.round((filled / visible.length) * 100) };
}

// ── Public: render a single section ──────────────────────────────────
export function SchemaSection({ section, answers, onSectionChange, tidPrefix = 'si' }) {
  const secAns = answers[section.key] || {};
  const setField = (key, value) => {
    const next = { ...secAns, [key]: value };
    onSectionChange(section.key, next);
  };

  return (
    <div className="space-y-5">
      {section.hint ? (
        <div className="rounded-md border border-[#E3DFD7] bg-[#FAF8F4] p-3">
          <p className="text-[13px] text-[#5C5955] leading-relaxed">{section.hint}</p>
        </div>
      ) : null}
      {(section.fields || []).length === 0 ? (
        <p className="text-sm text-[#8A8681]">No fields in this section yet.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {(section.fields || []).map(f => (
            <div key={f.key} className={f.type === 'textarea' || f.type === 'multi' || f.type === 'multi3' ? 'md:col-span-2' : ''}>
              <FieldBlock
                field={f}
                sectionKey={section.key}
                value={secAns[f.key]}
                sectionAnswers={secAns}
                onChange={(v) => setField(f.key, v)}
                tidPrefix={tidPrefix}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Section nav strip (left rail) ────────────────────────────────────
export function SectionNav({ schema, activeKey, answers, onSelect, tidPrefix = 'si' }) {
  const stats = useMemo(() => schema.map(s => ({ key: s.key, ...computeSectionCompleteness(s, answers) })), [schema, answers]);
  return (
    <div className="space-y-1" data-testid={`${tidPrefix}-section-nav`}>
      {schema.map((s, i) => {
        const st = stats[i];
        const active = s.key === activeKey;
        const dotColor = st.total === 0
          ? '#C8A87B'
          : st.pct >= 100 ? '#4E7A6B' : st.pct > 0 ? '#C89B7B' : '#B5B0A8';
        return (
          <button
            key={s.key}
            type="button"
            onClick={() => onSelect(s.key)}
            data-testid={`${tidPrefix}-nav-${s.key}`}
            className={[
              'w-full text-left px-3 py-2 rounded-md border transition-all flex items-center gap-2',
              active ? 'bg-[#2C2A29] text-white border-[#2C2A29]' : 'bg-white text-[#3B3936] border-[#EBE8E3] hover:border-[#C89B7B]',
            ].join(' ')}
          >
            <span className="inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: dotColor }} />
            <span className="text-xs font-medium truncate">{s.title}</span>
            {st.total > 0 ? (
              <span className={active ? 'ml-auto text-[10px] opacity-80' : 'ml-auto text-[10px] text-[#8A8681]'}>
                {st.filled}/{st.total}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

// ── Validation helpers ───────────────────────────────────────────────
export function validateSchema(schema, answers) {
  const errors = [];
  for (const s of schema) {
    const secAns = answers[s.key] || {};
    for (const f of (s.fields || [])) {
      if (!isVisible(f, secAns)) continue;
      if (!f.required) continue;
      const v = secAns[f.key];
      const empty = (f.type === 'checkbox') ? !v
        : Array.isArray(v) ? v.length === 0
        : typeof v === 'number' ? false
        : !(v && String(v).trim());
      if (empty) {
        errors.push({ section: s.key, sectionTitle: s.title, field: f.key, label: f.label });
      }
    }
  }
  return errors;
}

export function overallCompleteness(schema, answers) {
  let filled = 0, total = 0;
  for (const s of schema) {
    const st = computeSectionCompleteness(s, answers);
    filled += st.filled; total += st.total;
  }
  return total === 0 ? 0 : Math.round((filled / total) * 100);
}
